import { useEffect, useState } from "react";
import {
  listMemoryReview,
  decideMemoryReview,
  listConversationSummaries,
  type MemoryReviewItem,
  type ConversationSummary,
} from "@/api/memory";
import {
  Button,
  PocketRow,
  PocketSection,
  PocketStatement,
} from "@/components/ui";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsMemoryMessages } from "@/i18n/messages/settings-memory";
import type en from "@/i18n/messages/settings-memory/en";
import { useMemoryStore } from "@/stores/memory-store";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError } from "./settings-text-field";

const VALUE_LABEL_KEYS = {
  statement: "valueStatement",
  title: "valueTitle",
  name: "valueName",
  description: "valueDescription",
  value: "valueValue",
  content: "valueContent",
  body: "valueBody",
  steps: "valueSteps",
  purpose: "valuePurpose",
  key: "valueKey",
} as const satisfies Record<string, keyof typeof en>;
function readableValues(value: Record<string, unknown>): [string, string][] {
  const nested = value.payload;
  const values =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? readableValues(nested as Record<string, unknown>)
      : [];
  for (const [key, labelKey] of Object.entries(VALUE_LABEL_KEYS)) {
    const label = translate(settingsMemoryMessages, labelKey);
    const entry = value[key];
    if (
      typeof entry === "string" ||
      typeof entry === "number" ||
      typeof entry === "boolean"
    )
      values.push([label, String(entry)]);
    else if (Array.isArray(entry)) {
      const lines = entry.filter((item) => typeof item === "string");
      if (lines.length) values.push([label, lines.join("\n")]);
    }
  }
  return values;
}
function ValueDetails({
  title,
  value,
}: {
  title: string;
  value: Record<string, unknown>;
}) {
  const fields = readableValues(value);
  if (!fields.length) return null;
  return (
    <PocketSection title={title}>
      {fields.map(([label, text], index) => (
        <div key={`${label}-${index}`} className="px-2 py-1">
          <p className="text-appkit-caption text-muted">{label}</p>
          <p className="whitespace-pre-wrap break-words text-appkit-body text-primary">
            {text}
          </p>
        </div>
      ))}
    </PocketSection>
  );
}
export function MemoryProposalPage({ reviewId }: { reviewId?: string }) {
  const t = useT(settingsMemoryMessages);
  const tc = useT(commonMessages);
  const nav = useSettingsNav();
  const [item, setItem] = useState<MemoryReviewItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const scope = captureDesktopChatAuthScopeGuard();
    setItem(null);
    setLoading(true);
    setError(null);
    void (async () => {
      let cursor: string | undefined;
      do {
        const page = await listMemoryReview({ state: "pending", cursor });
        if (!active || !scope()) return;
        const found = page.items.find((candidate) => candidate.id === reviewId);
        if (found) {
          setItem(found);
          return;
        }
        cursor = page.pagination.next_cursor ?? undefined;
      } while (cursor);
      setItem(null);
    })()
      .catch(() => {
        if (active && scope())
          setError(translate(settingsMemoryMessages, "proposalLoadError"));
      })
      .finally(() => {
        if (active && scope()) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reviewId, attempt]);
  async function decide(decision: "accept" | "reject") {
    if (!item || busy) return;
    setBusy(true);
    setError(null);
    const scope = captureDesktopChatAuthScopeGuard();
    try {
      await decideMemoryReview(item.id, decision);
      if (!scope()) return;
      void useMemoryStore.getState().refresh();
      window.dispatchEvent(new Event("denker-memory-review-changed"));
      nav.back();
    } catch {
      if (scope()) setError(t("decisionSaveError"));
    } finally {
      if (scope()) setBusy(false);
    }
  }
  if (loading)
    return <PocketStatement size="panel" title={t("loadingMemory")} />;
  if (!item)
    return (
      <PocketStatement
        size="panel"
        title={error ?? t("noLongerWaiting")}
        action={
          error ? (
            <Button
              size="sm"
              data-testid="memory-proposal-retry"
              onClick={() => setAttempt((n) => n + 1)}
            >
              {tc("retry")}
            </Button>
          ) : undefined
        }
      />
    );
  return (
    <div data-testid="settings-page-memory-proposal">
      <PocketStatement
        size="panel"
        title={item.title}
        line={item.reason_label}
      />
      {item.source_quote ? (
        <blockquote
          className="px-2 py-3 text-appkit-body text-muted"
          data-testid="memory-proposal-evidence"
        >
          {item.source_quote}
        </blockquote>
      ) : null}
      {item.current_value ? (
        <PocketSection title={t("current")} testId="memory-proposal-current">
          <p className="whitespace-pre-wrap break-words px-2 py-1 text-appkit-body text-primary">
            {item.current_value}
          </p>
        </PocketSection>
      ) : null}
      <ValueDetails title={t("proposed")} value={item.proposed_value} />
      <SettingsFieldError error={error} testId="memory-proposal-error" />
      <div className="flex gap-2 px-2 py-3">
        <Button
          size="sm"
          disabled={busy}
          data-testid="memory-proposal-accept"
          onClick={() => void decide("accept")}
        >
          {t("accept")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          data-testid="memory-proposal-reject"
          onClick={() => void decide("reject")}
        >
          {t("reject")}
        </Button>
      </div>
    </div>
  );
}

export function MemorySummariesPage() {
  const t = useT(settingsMemoryMessages);
  const tc = useT(commonMessages);
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const scope = captureDesktopChatAuthScopeGuard();
    setError(false);
    void listConversationSummaries({ days: 7, limit: 10 })
      .then((rows) => {
        if (active && scope()) setItems(rows);
      })
      .catch(() => {
        if (active && scope()) setError(true);
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  return (
    <div data-testid="settings-page-memory-summaries">
      <PocketStatement
        size="panel"
        title={
          error
            ? t("summariesLoadError")
            : items === null
              ? t("loadingSummaries")
              : items.length
                ? t("recentSummaries")
                : t("noSummaries")
        }
        line={
          items?.length
            ? t("summariesCount", { count: items.length })
            : undefined
        }
        action={
          error ? (
            <Button
              size="sm"
              data-testid="memory-summaries-retry"
              onClick={() => setAttempt((n) => n + 1)}
            >
              {tc("retry")}
            </Button>
          ) : undefined
        }
      />
      <PocketSection>
        {items?.map((item) => (
          <PocketRow
            key={item.id}
            testId={`memory-summary-${item.id}`}
            title={item.title}
            below={
              <p className="whitespace-pre-wrap text-appkit-body text-muted">
                {item.content}
              </p>
            }
          />
        ))}
      </PocketSection>
    </div>
  );
}
